import { Component, OnInit, EventEmitter, Input, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, ViewRef, HostListener } from '@angular/core';
import { DisplayAction } from 'src/app/model/map-actions/display-actions';
import { CastingTypeFormat } from './formats/casting-type-formats-sel/casting-type-formats-sel.component';
import { CastingFormatType } from './formats/casting-formats-sel/casting-formats-sel.component';

import { FractionalValue } from './formats/fractional-values-sel/fractional-values-sel.component';
import { PlusMinusOption } from '../plus-minus-sel/plus-minus-sel.component';
import { CurrencyFormat } from '../currency-formats-sel/currency-formats-sel.component';
import { NegativeNumberFormatting } from './formats/negative-numbers-sel/negative-numbers-sel.component';
import { CastingLinkValue } from './formats/casting-link/casting-link.component';
import { CastingTextInputValue, CastingTextDisplayValue } from './formats/casting-text/casting-text.component';
import { CastingBooleanValue } from './formats/casting-boolean/casting-boolean.component';
import { SLLayer } from 'src/app/model/sl-layer';
import { ModalMessagerService } from 'src/app/modal-messager/modal-messager.service';
import { CurrencyCode } from 'src/app/model/api-services/qualifiers/number-qualifier.service';
import { AttributesService } from 'src/app/model/api-services/attributes.service';
import { ResultsService } from 'src/app/services/results.service';
import { GeolocationProperty, GeolocationPropertyOption } from 'src/app/model/api-services/qualifiers/geo-location-qualifier-service';
import { MediaQualifierVal } from 'src/app/model/api-services/qualifiers/MediaQualifier.service';
import { CastingMediaValue } from './formats/casting-media-sel/casting-media-sel.component';


export type RangeType = 'number' | 'date';
export type CastingMode = 'entry' | 'display';
export type CastingRange = {
  min?: number;
  max?: number;
  minDate?: string;
  maxDate?: string;
}
export type CastingCurrencyValue = {
  format: CurrencyFormat;
  code: CurrencyCode;
}
export type CastingValue = {
  type: CastingFormatType;
  format?: CastingTypeFormat;
  plusMinus?: PlusMinusOption;
  fixedDigits?: number;
  precision?: number;
  currency?: CastingCurrencyValue;
  negFormat?: NegativeNumberFormatting;
  fractional?: FractionalValue;
  link?: CastingLinkValue;
  text?: CastingTextInputValue;
  boolean?: CastingBooleanValue;
  range?: CastingRange;
  require?: boolean;
  geoLocPart?: GeolocationPropertyOption,
  mediaValue?: CastingMediaValue
  dateDefault?: '' | 'NOW';
}

@Component({
  selector: 'app-att-casting-view',
  templateUrl: './att-casting-view.component.html',
  styleUrls: ['./att-casting-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttCastingViewComponent implements OnInit {
  public name = 'Casting'
  public layer: SLLayer;
  public attribute: any;
  public mode: CastingMode = 'display';
  public localMenuOpen = false;


  public castingFormat: CastingFormatType = '';
  public requireAtt = true;
  public useValidation = true;
  public castingFormatLabel = '';
  public typeFormat: CastingTypeFormat = 'comma-dot';
  public plusMinus: PlusMinusOption = '+-';
  public currencyCode: CurrencyCode = 'USD';
  public currencyFormat: CurrencyFormat = 'S#C';
  public isNegativable = true;
  public negFormat: NegativeNumberFormatting = {
    color: '#000000',
    format: 'neg'
  }
  public fractionalValue: FractionalValue = {
    mode: 'percent',
    denom: '100',
    labelMode: 'label'
  }
  public linkValue: CastingLinkValue = {
    base: '',
    display: 'label',
    label: 'click to view'
  }
  public textValue: CastingTextInputValue | CastingTextDisplayValue = {
    input: 'single',
    maxChars: Number.MAX_SAFE_INTEGER,
    minChars: 0
  }

  public boolValue: CastingBooleanValue = {
    format: 'On,Off',
    trueValue: 'On',
    falseValue: 'Off',
    formatCase: 'Aa',
    labelMode: 'toggle-switch'
  }

  public mediaValue: CastingMediaValue = {
    mediaOption: 'photo',
    serviceId: '',
    pathName: '',
    relPath: '',
    displayPath: '',
    altId: ''
  }

  public validateRegEx = true;
  public hasRange = true;
  public rangeMin = 0;
  public rangeMax: number = Number.MAX_SAFE_INTEGER;
  public dateRangeMin: string = (new Date() as any).toString('yyyy-MM-dd');
  public dateRangeMax: string = (new Date() as any).toString('yyyy-MM-dd');
  public dateDefault: '' | 'NOW' = '';
  public rangeType: RangeType = 'number';
  public precision = 4;
  public fixedDigits = 2;

  public geoLocPart: GeolocationPropertyOption = '';

  public useFormatSel = false;
  public usePlusMinus = false;
  public useFixed = false;
  public usePrecision = false;
  public useCurrency = false;
  public useNegative = false;
  public useFraction = false;
  public useLink = false;
  public useText = false;
  public useBoolean = false;
  public useRange = false;
  public useDateRange = false;
  public useNumberRange = false;
  public useGeoLocPart = false;
  public useMedia = false;

  @Input('value')
  public set value(val: CastingValue) {
    this.Clear(); // this.ResetValues();
    if (!val) {
      return;
    }
    this.castingFormat = val.type;
    if (!!val.format) {
      this.typeFormat = val.format;
    }
    if (!!val.geoLocPart) {
      this.useFormatSel = false;
      this.geoLocPart = val.geoLocPart;
      this.useGeoLocPart = true;
    }
    if (!!val.plusMinus) {
      this.plusMinus = val.plusMinus;
    }
    if (!!val.fixedDigits) {
      this.fixedDigits = val.fixedDigits;
      this.useFixed = true;
    } else {
      this.useFixed = false;
    }
    if (!!val.precision) {
      this.precision = val.precision;
      this.usePrecision = true;
    } else {
      this.usePrecision = false;
    }
    if (!!val.currency) {
      this.useFormatSel = true;
      this.currencyFormat = val.currency.format;
      this.currencyCode = val.currency.code;
    }
    if (!!val.negFormat) {
      this.negFormat = val.negFormat;
      this.useNegative = true;
    } else {
      this.useNegative = false;
    }
    if (!!val.fractional) {
      this.fractionalValue = val.fractional;
      this.useFraction = true;
    } else {
      this.useFraction = false;
    }
    if (!!val.link) {
      this.linkValue = val.link;
      this.useLink = true;
    } else {
      this.useLink = false;
    }
    if (!!val.boolean) {
      this.boolValue = val.boolean;
      this.useBoolean = true;
    } else {
      this.useBoolean = false;
    }
    if (!!val.mediaValue) {
      this.mediaValue = val.mediaValue;
      this.useMedia = true;
    } else {
      this.useMedia = false;
    }
    if (!!val.range) {
      switch (this.rangeType) {
        case 'date':
          if (!!val.range.maxDate) {
            this.hasRange = true;
            this.dateRangeMin = val.range.minDate;
            this.dateRangeMax = val.range.maxDate;

          } else {
            this.hasRange = false;
          }
          break;
        case 'number':
        default:
          if (!!val.range.min) {
            this.hasRange = true;
            this.rangeMin = val.range.min;
            this.rangeMax = val.range.max;
          } else {
            this.hasRange = false;
          }
          break;
      }

    } else {
      this.hasRange = false;
    }
    if (!!val.require) {
      this.requireAtt = val.require;
    } else {
      this.requireAtt = false;
    }
    if (!!val.dateDefault) {
      this.dateDefault = val.dateDefault;
    } else {
      this.dateDefault = '';
    }

    this.isNegativable = this.plusMinus !== '+';
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }

  }
  public get value(): CastingValue {
    const value: CastingValue = {
      type: this.castingFormat
    }
    if (this.useFormatSel === true) {
      value.format = this.typeFormat;
    }
    if (this.usePlusMinus === true) {
      value.plusMinus = this.plusMinus;
    }
    if (this.useFixed === true) {
      value.fixedDigits = this.fixedDigits;
    }
    if (this.usePrecision === true) {
      value.precision = this.precision;
    }
    if (this.useCurrency === true) {
      value.currency = {
        format: this.currencyFormat,
        code: this.currencyCode
      }
    }
    if (this.useNegative === true) {
      value.negFormat = this.negFormat;
    }
    if (this.useFraction === true) {
      value.fractional = this.fractionalValue;
    }
    if (this.useLink === true) {
      value.link = this.linkValue;
    }
    if (this.useText === true) {
      value.text = this.textValue as CastingTextInputValue;
    }
    if (this.useBoolean === true) {
      value.boolean = this.boolValue;
    }
    if (this.useRange === true) {
      if (this.hasRange === true) {
        switch (this.rangeType) {
          case 'date':
            value.range = {
              minDate: this.dateRangeMin,
              maxDate: this.dateRangeMax
            }
            break;
          default:
          case 'number':
            value.range = {
              min: this.rangeMin,
              max: this.rangeMax
            }
        }
      }
    }
    if (this.useGeoLocPart === true) {
      value.geoLocPart = this.geoLocPart;
    }
    if (this.useMedia === true) {
      value.mediaValue = this.mediaValue;
    }
    value.require = this.requireAtt;
    if (this.isDate === true) {
      value.dateDefault = this.dateDefault;
    }
    return value;
  }
  public get isDate() {
    return this.castingFormat === 'date';
  }
  public get showDefaultDate(): boolean {
    return this.isDate && (this.mode === 'entry');
  }
  public get isTime() {
    return this.castingFormat === 'time';
  }
  public valueChange = new EventEmitter<CastingValue>();


  constructor(
    private messager: ModalMessagerService,
    private resultsSvc: ResultsService,
    private domNode: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
  }
  @HostListener('document:click', ['$event'])
  public HandleDocumentClick(event: MouseEvent) {
    if (!this.domNode.nativeElement.contains(event.target as Node)) {
      this.CloseMenus();
    }
  }
  public ToggleLocalMenu(event: Event) {
    event.stopPropagation();
    this.localMenuOpen = !this.localMenuOpen;
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }
  public CloseMenus() {
    this.localMenuOpen = false;
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.castingFormat = ''

    this.Clear();
  }

  Back() {
    this.messager.Confirm(
      'Clicking Yes below will return you to the previous panel view and will ignore any changes you have made here. If you want to keep your changes click no below and use the continue button at the bottom of the panel to keep your changes and return to the previous view.',
      () => {
        DisplayAction.SetPrimaryView('layer-attribute-view');
      }, () => { }, 'Ignore changes?');

  }

  Upload() {
  }

  Download() {
  }
  RequestExport() {
    this.CloseMenus();
    this.messager.RequestExport({
      extension: 'slcast', mimeType: 'text/json',
      contents: JSON.stringify({ layer: this.layer.id, attribute: this.attribute.name, mode: this.mode, casting: this.value }),
      defaultFileName: this.layer.name + '-' + this.attribute.name + '-' + this.mode
    }, 'Export casting configuration for layer ' + this.layer.name + ' @' + this.attribute, () => { }, () => { }, 'Configuration Export ...');
  }
  RequestImport() {
    this.CloseMenus();
    this.messager.RequestImport({
      acceptedExtentions: ['slcast'],
      encoding: 'utf-8',
      readType: 'text'
    }, 'Import casting configuration', (response: any) => {
      if (!!response.contents) {
        const jsonContent = JSON.parse(response.contents);
        if (!!jsonContent.casting) {
          this.value = jsonContent.casting;
          this.cdr.detectChanges();
          return;
        }
      }
    }, (message: any) => {
      this.messager.Notify('Unable to import file - ' + message.reason, true, 'Import error');
    }, 'Configuration Import ...');
  }

  ToggleRange(hasRange: boolean) {
    this.hasRange = hasRange;
    this.cdr.detectChanges();
  }
  FixedChange(value: string) {
    if (value === '') {
      return;
    }
    if (Number(value) > 21) {
      this.fixedDigits = 21;
    }
    if (Number(value) < 0) {
      this.fixedDigits = 0;
    }
    this.cdr.detectChanges();
  }
  PrecisionChange(value: string | number) {
    if (value === '') {
      return;
    }
    if (Number(value) > 21) {
      this.precision = 21;
    }
    if (Number(value) < 1) {
      this.precision = 1;
    }
    this.cdr.detectChanges();
  }
  ResetValues() {
    this.useFormatSel = false;
    this.usePlusMinus = false;
    this.useFixed = false;
    this.usePrecision = false;
    this.useCurrency = false;
    this.useNegative = false;
    this.useFraction = false;
    this.useLink = false;
    this.useText = false;
    this.useBoolean = false;
    this.useRange = false;
    this.useGeoLocPart = false;
    this.useMedia = false;
    this.cdr.detectChanges();
  }
  FormatChanged() {
    this.ResetValues();
    this.rangeType = 'number';

    switch (this.castingFormat) {

      case 'number-dec':
      case 'number-general':
        this.useFormatSel = true;
        this.usePlusMinus = true;
        this.useFixed = true;
        this.useNegative = true;
        this.useRange = true;
        break;
      case 'number-sci':
        this.useFormatSel = true;
        this.usePlusMinus = true;
        this.usePrecision = true;
        this.useNegative = true;
        this.useRange = true;
        break;
      case 'number-hex':
      case 'number-octal':
      case 'number-binary':
        this.useFormatSel = true;
        this.useRange = true;
        break;
      case 'number-fraction':
        this.usePlusMinus = true;
        this.useFixed = true;
        this.useNegative = true;
        this.useFraction = true;
        const fracVal: FractionalValue = {
          denom: '2',
          mode: 'numerator',
          labelMode: 'label'
        }
        if (
          this.attribute.casting &&
          this.attribute.casting[this.mode].fractional &&
          this.attribute.casting[this.mode].fractional.mode
        ) {
          if (this.attribute.casting[this.mode].fractional.mode === 'percent') {
            this.fractionalValue = fracVal;
          } else {
            this.fractionalValue = this.attribute.casting[this.mode].fractional;
          }
        } else {
          this.fractionalValue = fracVal;
        }
        this.useRange = true;
        break;
      case 'number-geoloc-part':
        this.useFormatSel = false;
        this.useGeoLocPart = true;
        break;
      case 'number-phone':
        this.useFormatSel = true;
        break;
      case 'number-currency':
        this.useFormatSel = true;
        this.usePlusMinus = true;
        this.useFixed = true;
        this.useCurrency = true;
        this.useNegative = true;
        this.useRange = true;
        break;
      case 'number-percentage':
        this.useFixed = true;
        this.useFraction = true;
        this.useFixed = true;
        const pctVal: FractionalValue = {
          denom: '100',
          mode: 'percent',
          labelMode: 'label'
        }
        if (!!this.attribute.casting[this.mode].fractional) {
          if (this.attribute.casting[this.mode].fractional !== 'percent') {
            this.fractionalValue = pctVal;
          } else {
            this.fractionalValue = this.attribute.casting[this.mode].fractional;
          }
        } else {
          this.fractionalValue = pctVal;
        }
        break;
      case 'number-geoloc-part':
        this.useGeoLocPart = true;
        break;
      case 'number-zipcode':
        this.useFormatSel = true;
        break;
      case 'date':
        this.useFormatSel = true;
        this.useRange = true;
        this.rangeType = 'date';
        break;
      case 'time':
        this.useFormatSel = true;
        this.useRange = false;
        break;
      case 'text':
        this.useText = true;
        break;
      case 'boolean':
        this.useBoolean = true;
        break;
      case 'link':
        this.useLink = true;
        break;
      case 'media':
        if (!!this.attribute.casting && this.attribute.casting[this.mode] && !!this.attribute.casting[this.mode].mediaValue) {
          this.mediaValue = this.attribute.casting[this.mode].mediaValue;
        }
        this.useMedia = true;
        break;
    }

    this.useDateRange = this.rangeType === 'date';
    this.useNumberRange = this.rangeType === 'number';

    this.Update();
  }
  SetAttribute(layer: SLLayer, attribute: any, mode: CastingMode) {
    this.castingFormat = ''

    this.Clear();

    this.layer = layer;
    this.attribute = attribute;
    this.mode = mode;
    this.useRange = this.useRange;
    this.hasRange = this.hasRange;
    this.name = 'Casting @' + attribute.display + ' as ...';
    if (!!attribute.casting) {
      if (!!attribute.casting[mode]) {
        this.value = attribute.casting[mode];

        if (!!this.attribute.casting.entry) {
          if (!!this.attribute.casting.entry.range) {
            if (this.attribute.casting.entry.range.maxDate) {
              this.dateRangeMax = this.attribute.casting.entry.range.maxDate;
              this.dateRangeMin = this.attribute.casting.entry.range.minDate;
              this.hasRange = true;
            } else {
              this.hasRange = false;
            }
          } else {
            this.hasRange = false;
          }

        }
      }
    } else {
      this.castingFormat = '';

    }
    this.FormatChanged();
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }

  }
  UpdatePlusMinus(val) {
    this.plusMinus = val;
    this.Update();
  }
  Update() {
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
    this.isNegativable = (this.plusMinus !== '+');
    if (this.plusMinus === '+') {
      if (this.rangeMin < 0) {
        this.rangeMin = 0;
      }
    }
    if (this.plusMinus === '-') {
      if (this.rangeMax > 0) {
        this.rangeMax = 0;
      }
    }
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
    this.valueChange.emit(this.value);
  }
  Continue() {
    this.messager.Confirm('Continuing will keep your changes for this attribute locally. The changes will be saved only when attributes are saved for this layer to the server. If you wish to continue click Yes below, otherwise click No and use the red x button at the top right corner of the panel to return to the attribute view without committing changes',
      () => {
        if (!this.attribute.casting) {
          this.attribute.casting = {};
        }
        this.attribute.casting[this.mode] = this.value;
        this.resultsSvc.history.UpdateMomentsWithLayer(this.layer);
        DisplayAction.SetPrimaryView('layer-attribute-view');
      }, () => { },
      'Keep current changes?'
    );
  }
  Clear() {
    this.ResetValues();
    this.fixedDigits = 2;
    this.precision = 4;

    this.negFormat = {
      color: '#000000',
      format: 'neg'
    }
    this.fractionalValue = {
      mode: 'percent',
      denom: '100',
      labelMode: 'label'
    }
    this.linkValue = {
      base: '',
      display: 'label',
      label: 'click to view'
    }
    this.textValue = {
      input: 'single',
      maxChars: Number.MAX_SAFE_INTEGER,
      minChars: 0
    }
    this.boolValue = {
      format: 'On,Off',
      trueValue: 'On',
      falseValue: 'Off',
      formatCase: 'Aa',
      labelMode: 'toggle-switch'
    }
    this.mediaValue = {
      mediaOption: 'photo',
      pathName: '',
      relPath: '',
      serviceId: '',
      displayPath: '',
      altId: 'gid'
    }
    this.cdr.detectChanges();
  }
  public UpdateHeight(primaryHeight: number, panelMax: number) {
    primaryHeight = primaryHeight;
    this.domNode.nativeElement.style.setProperty('--panel-max', panelMax + 'px');
  }
}
